import React, { FC, useCallback } from "react";
import { connect } from "react-redux";

import Form, { FormInstance } from "antd/es/form";
import cn from "classnames";
import { Draggable } from "react-beautiful-dnd";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldVisibility from "@mapmycustomers/shared/enum/FieldVisibility";
import {
  EntityTypesSupportingFieldCustomization,
  EntityTypeSupportingGroups,
  Group,
} from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import FunnelStageValue from "@mapmycustomers/shared/types/FunnelStageValue";
import FormLayout, {
  ChildLayout,
  LayoutSchemaField,
} from "@mapmycustomers/shared/types/layout/FormLayout";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { Alert } from "@mapmycustomers/ui";

import Divider from "@app/component/FormFields/components/Divider";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import { getActivityTypes } from "@app/store/activity";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { getGroupsForEntity } from "@app/store/groups";
import { getCurrentUser } from "@app/store/iam";
import { getAllTeamsOfEntireOrganization, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";

import { getActiveEntityType, getEditedChildLayoutId } from "../../store";
import LayoutRowActions from "../LayoutRowActions";
import LayoutRowDragHandle from "../LayoutRowDragHandle";

import styles from "./Canvas.module.scss";
import useFormFieldRenderer from "./util/useFormFieldRenderer";

export interface LayoutSchemaFieldProps {
  activeChildLayout?: ChildLayout["id"];
  activeSchema: LayoutSchemaField[];
  activityTypes: ActivityType[];
  allTeams: Team[];
  childLayout: ChildLayout | undefined;
  currentUser: User;
  defaultFunnelStage: Partial<FunnelStageValue>;
  dragOverIndex: null | number;
  entityType: EntityTypesSupportingFieldCustomization;
  form: FormInstance<any>;
  funnels: Funnel[];
  funnelStages: Record<Funnel["id"], Stage[]>;
  groups: Group[];
  index: number;
  isOver: boolean;
  layout?: FormLayout;
  onChangeSchemaField: (schemaField: LayoutSchemaField) => void;
  row: LayoutSchemaField;
  showAddressAlert: () => void;
  users: User[];
}

const SchemaField: FC<LayoutSchemaFieldProps> = ({
  activeChildLayout,
  activeSchema,
  activityTypes,
  allTeams,
  childLayout,
  currentUser,
  defaultFunnelStage,
  dragOverIndex,
  entityType,
  form,
  funnels,
  funnelStages,
  groups,
  index,
  isOver,
  layout,
  onChangeSchemaField,
  row,
  showAddressAlert,
  users,
}) => {
  const analyticsIssuer = useAnalytics();
  const intl = useIntl();
  const fieldAnalytics = useExtendedAnalytics("Field", analyticsIssuer);
  const fieldModel = getFieldModelByEntityType(entityType);

  const activityTypeDefaultValue = Form.useWatch(ActivityFieldName.ACTIVITY_TYPE, form);

  const isDivider = row.fieldType === "divider";
  const field = isDivider ? undefined : fieldModel.getByPlatformName(row.field);
  const rowKey = `${activeChildLayout ?? layout?.id ?? ""}.${row.fieldType}.${row.field}${
    row.fieldType === "divider" ? `.${row.displayOrder}` : ""
  }`;

  const isFixed = !field?.isMovableInLayout && !isDivider;
  const showNoAccessAlert =
    row.required &&
    field?.schemaDetails?.visibility &&
    ([FieldVisibility.PRIVATE, FieldVisibility.PUBLIC].includes(field.schemaDetails.visibility) ||
      (field.schemaDetails.visibility === FieldVisibility.TEAMS &&
        (field.schemaDetails.teamVisibility ?? []).some(({ visibility }) =>
          [FieldVisibility.PRIVATE, FieldVisibility.PUBLIC].includes(visibility)
        )));

  const render = useFormFieldRenderer(
    activeSchema,
    childLayout,
    activityTypes,
    activityTypeDefaultValue,
    entityType,
    funnels,
    funnelStages,
    defaultFunnelStage,
    groups,
    currentUser,
    users,
    allTeams
  );

  const handleRowDelete = useCallback(
    (schemaField: LayoutSchemaField) => {
      if (schemaField.field === "address") {
        showAddressAlert();
      }
    },
    [showAddressAlert]
  );

  if (field?.hasFeature(FieldFeature.FORM_RENDERED_WITH_OTHER_FIELD)) {
    // Render non-draggable empty placeholder to keep consistency
    // with index-based keys for other draggable items
    return (
      <div key={rowKey}>
        <Draggable
          draggableId={String(row.displayOrder)}
          index={index}
          isDragDisabled
          key={row.displayOrder}
        >
          {(provided) => <div {...provided.draggableProps} ref={provided.innerRef} />}
        </Draggable>
      </div>
    );
  }

  return (
    <div key={rowKey}>
      {isOver && (
        <div
          className={cn(styles.dropPlaceholder, {
            [styles.active]: index === dragOverIndex,
          })}
        />
      )}
      <Draggable
        draggableId={String(row.displayOrder)}
        index={index}
        isDragDisabled={isFixed}
        key={row.displayOrder}
      >
        {(provided, snapshot) => (
          <>
            <div
              {...provided.draggableProps}
              ref={provided.innerRef}
              style={{
                ...provided.draggableProps.style,
                transform: snapshot.isDragging
                  ? provided.draggableProps.style?.transform
                  : "translate(0px, 0px)",
                transition: "all 0.001s ease-out",
              }}
            >
              <div
                className={cn(styles.item, {
                  [styles.dragging]: isOver || snapshot.isDragging,
                })}
              >
                <LayoutRowDragHandle
                  className={styles.handle}
                  dragging={isOver || snapshot.isDragging}
                  dragHandleProps={provided.dragHandleProps}
                  fixed={isFixed}
                />

                <div
                  className={cn(styles.row, {
                    [styles.full]: isDivider || field?.formProperties?.fullWidth,
                  })}
                >
                  <div className={styles.control}>
                    {isDivider ? <Divider designMode /> : <>{render(field, row)}</>}
                  </div>
                  {showNoAccessAlert && !snapshot.isDragging && (
                    <Alert
                      message={intl.formatMessage(
                        {
                          id: "settings.formLayouts.canvas.noAccessAlert.title",
                          defaultMessage:
                            "At least one user in your organization does not have access to this field’s values. If this layout is shared with them, they will not be able to edit this record. <a>Manage Field Access</a>",
                          description: "Field access alert",
                        },
                        {
                          a: (text) => (
                            <Link to={`${Path.SETTINGS}/${SettingPath.FORMS_MANAGE_FIELDS}`}>
                              {text}
                            </Link>
                          ),
                        }
                      )}
                      showIcon
                      type="warning"
                    />
                  )}
                </div>

                {(!isFixed ||
                  field?.isDefaultValueAllowed ||
                  // we allow changing different required value for the Person Name fields
                  field?.name === PersonFieldName.FIRST_NAME) &&
                  !isOver &&
                  !snapshot.isDragging && (
                    <LayoutRowActions
                      analyticsIssuer={fieldAnalytics}
                      className={styles.actions}
                      onDelete={handleRowDelete}
                      onEditDefaultValue={onChangeSchemaField}
                      schemaField={row}
                    />
                  )}
              </div>
            </div>

            {snapshot.isDragging && (
              <div
                className={cn(styles.item, {
                  [styles.dragging]: isOver || snapshot.isDragging,
                })}
              >
                <div
                  className={cn(styles.row, {
                    [styles.full]: isDivider || field?.formProperties?.fullWidth,
                  })}
                >
                  <div className={styles.control}>
                    {isDivider ? <Divider designMode /> : <>{render(field, row)}</>}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Draggable>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const entityType = getActiveEntityType(state);
  return {
    activeChildLayout: getEditedChildLayoutId(state),
    activityTypes: getActivityTypes(state),
    allTeams: getAllTeamsOfEntireOrganization(state),
    currentUser: getCurrentUser(state)!,
    entityType,
    funnels: getFunnels(state),
    funnelStages: getFunnelStages(state),
    groups: getGroupsForEntity(state)(entityType as EntityTypeSupportingGroups),
    users: getUsers(state),
  };
};

export default connect(mapStateToProps)(SchemaField);
